import type { GraphQLSupporterAddress } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { UserError } from '@sola/shared-utils/errors-user.ts';
import { userErrorBadParam } from '@sola/shared-utils/errors/misc.ts';
import {
  httpPatchWithStatus,
  httpPutWithStatus,
  isErrorResponse,
  isSuccessStatus,
} from '@sola/shared-utils/httpClient.ts';
import type { ErrorResponse } from '@sola/shared-utils/httpClient.ts';

import { MY_API_BASE } from '../constants.ts';

export type AccountSettingsMutationError = UserError | UserError[];

export interface AddressUpdateResponse {
  success: boolean;
  data?: GraphQLSupporterAddress | unknown;
  message?: string;
}
export interface ContactInfoPayload {
  firstName: string;
  lastName: string;
  preferredName?: string;
  email: string;
  phone?: string;
}

export interface ContactInfoUpdateResponse {
  success: boolean;
  data?: ContactInfoPayload | unknown;
  message?: string;
}

const routeAccountSettingsContact = (supporterId: string, contactId: string) =>
  `${MY_API_BASE}/supporters/${supporterId}/contacts/${contactId}`;

const routeAccountSettingsAddress = (supporterId: string) =>
  `${MY_API_BASE}/supporters/${supporterId}/address`;

/**
 * Update a supporter's mailing address.
 */
export const updateSupporterAddress = async ({
  globalId,
  address,
}: {
  globalId: string;
  address: Omit<GraphQLSupporterAddress, 'isUndeliverable'>;
}): Promise<AddressUpdateResponse | AccountSettingsMutationError> => {
  if (!globalId) {
    throw userErrorBadParam('globalId');
  }

  if (!address) {
    throw userErrorBadParam('address');
  }

  const { response, statusCode } = await httpPutWithStatus<
    AddressUpdateResponse | ErrorResponse
  >(routeAccountSettingsAddress(globalId), address, { signRequest: true });

  if (statusCode !== 200 || !response || isErrorResponse(response)) {
    throw new Error('Failed to update address');
  }

  return response as AddressUpdateResponse;
};

/**
 * Update a supporter's contact information.
 */
export const updateSupporterContactInfo = async ({
  contactId,
  globalId,
  contactInfo,
}: {
  contactId: string;
  globalId: string;
  contactInfo: ContactInfoPayload;
}): Promise<ContactInfoUpdateResponse | AccountSettingsMutationError> => {
  if (!contactId) {
    throw userErrorBadParam('contactId');
  }
  if (!globalId) {
    throw userErrorBadParam('globalId');
  }

  if (!contactInfo) {
    throw userErrorBadParam('contactInfo');
  }

  const { response, statusCode } = await httpPatchWithStatus<
    ContactInfoUpdateResponse | ErrorResponse
  >(routeAccountSettingsContact(globalId, contactId), contactInfo, {
    signRequest: true,
  });

  if (!isSuccessStatus(statusCode) || !response || isErrorResponse(response)) {
    throw new Error('Failed to update contact info');
  }

  return response as ContactInfoUpdateResponse;
};
